import React from 'react';
import { Form } from 'react-bootstrap';
import { getDirectionPoints } from './getDirectionPoints';
import { applyGradientToObject } from './applyGradientToObject';
import ColorSwatch from "./ColorSwatch";

export function renderGradientControls({
  shape,
  useGradient,
  setUseGradient,
  gradientStops,
  setGradientStops,
  gradientDirection,
  setGradientDirection,
  updateObject,
  scale
}) {

  const applyGradientUsingShape = (stops, direction = gradientDirection) => {
    if (!shape || stops.length < 2) return;

    const { w, h } = getGradientDimensions(shape, scale);

    // applyGradientToObject(
    //   shape.id,
    //   stops,
    //   direction,
    //   w,
    //   h
    // );
  };

  function getGradientDimensions(shape, scale = 1) {
    const type = (shape.type || "").toLowerCase();

    // ✅ RECT / IMAGE / anything with width & height
    if (shape.width != null && shape.height != null) {
      return {
        w: Math.max(1, shape.width),
        h: Math.max(1, shape.height)
      };
    }

    // ✅ CIRCLE
    if (type === "circle" && shape.radius != null) {
      const d = Math.max(1, shape.radius * 2);
      return { w: d, h: d };
    }

    // ✅ ELLIPSE
    if (type === "ellipse") {
      const w = Math.max(1, (shape.radiusX ?? 0) * 2);
      const h = Math.max(1, (shape.radiusY ?? 0) * 2);
      return { w, h };
    }

    // ✅ POLYGON / STAR / ARROW → derive from points
    if (Array.isArray(shape.points) && shape.points.length >= 4) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      for (let i = 0; i < shape.points.length; i += 2) {
        const px = shape.points[i];
        const py = shape.points[i + 1];

        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }

      const w = Math.max(1, (maxX - minX));
      const h = Math.max(1, (maxY - minY));

      return { w, h, minX, minY, maxX, maxY };
    }

    // ✅ Safety fallback
    return { w: 1, h: 1 };
  }



  const updateGradientOnShape = (stops, direction = gradientDirection) => {
    if (!shape) return;

    const dims = getGradientDimensions(shape);

    let start, end;

    if (Array.isArray(shape.points) && dims.minX !== undefined) {
      const [rawStart, rawEnd] = getDirectionPoints(direction, dims.w, dims.h);

      start = {
        x: rawStart.x + dims.minX,
        y: rawStart.y + dims.minY
      };

      end = {
        x: rawEnd.x + dims.minX,
        y: rawEnd.y + dims.minY
      };
    } else {
      [start, end] = getDirectionPoints(direction, dims.w, dims.h);
    }

    updateObject(shape.id, {
      useGradient: true,
      gradientDirection: direction,
      fill: null,
      fillLinearGradientColorStops: stops.flatMap((c, i) => [
        parseFloat((i / (stops.length - 1)).toFixed(4)),
        c
      ]),
      fillLinearGradientStartPoint: start,
      fillLinearGradientEndPoint: end
    });
  };

  const removeStop = (index) => {
    const newStops = gradientStops.filter((_, i) => i !== index);
    setGradientStops(newStops);
    updateGradientOnShape(newStops);
    const { w, h } = getGradientDimensions(shape, scale);

    // applyGradientToObject(
    //   shape.id,
    //   newStops,
    //   gradientDirection,
    //   w,
    //   h
    // );
  };

  const expandHexIfNeeded = (color) => {
    if (typeof color !== 'string') return '#ffffff';
    if (color.startsWith('#') && color.length === 4) {
      return (
        '#' +
        color[1] + color[1] +
        color[2] + color[2] +
        color[3] + color[3]
      );
    }
    return color;
  };


  const updateStops = (nextStops) => {
    setGradientStops(nextStops);

    updateGradientOnShape(nextStops);

    const { w, h } = getGradientDimensions(shape, scale);

    // applyGradientToObject(
    //   shape.id,
    //   nextStops,
    //   gradientDirection,
    //   w,
    //   h
    // );
  };


  return (
    <>
      {/* Gradient toggle */}
      <Form.Group className="mb-2" style={{ fontSize: '0.7rem' }}>
        <Form.Check
          type="switch"
          label="Gradient Fill"
          checked={useGradient}
          onChange={(e) => {
            const enabled = e.target.checked;
            setUseGradient(enabled);

            if (!enabled && gradientStops.length > 0) {
              updateObject(shape.id, {
                fillLinearGradientStartPoint: null,
                fillLinearGradientEndPoint: null,
                fillLinearGradientColorStops: null,
                fill: gradientStops[0]
              });
            } else if (enabled && gradientStops.length >= 2) {
              const { w, h } = getGradientDimensions(shape, scale);
              //
              // applyGradientToObject(
              //   shape.id,
              //   gradientStops,
              //   gradientDirection,
              //   w,
              //   h
              // );
            }
          }}
        />
      </Form.Group>

      {useGradient && (
        <>
          {/* Direction */}
          <Form.Group className="mb-2" style={{ fontSize: '0.7rem' }}>
            <Form.Label>Direction</Form.Label>
            <Form.Select
              value={gradientDirection}
              onChange={(e) => {
                const dir = e.target.value;
                setGradientDirection(dir);
                if (gradientStops.length >= 2) {
                    applyGradientUsingShape(gradientStops, gradientDirection);
                }
              }}
            >
              <option value="diagonal">Diagonal</option>
              <option value="horizontal">Horizontal</option>
              <option value="vertical">Vertical</option>
            </Form.Select>
          </Form.Group>

          {/* Stops */}
          <Form.Group className="mb-2" style={{ fontSize: '0.7rem' }}>
            <Form.Label>Stops</Form.Label><br/>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}
            >
              <tbody>
                {gradientStops.map((color, index) => {
                  const resolvedColor =
                    color === 'rgba(0,0,0,0)' || color === 'transparent'
                      ? 'transparent'
                      : expandHexIfNeeded(color);

                  return (
                    <tr key={index}>
                      {/* 🎨 Color */}
                      <td style={{ width: 34, paddingBottom: 6 }}>
                        <ColorSwatch
                          value={resolvedColor}
                          allowTransparent
                          size={24}
                          onChange={(nextColor) => {
                            const next = [...gradientStops];
                            next[index] = nextColor || 'rgba(0,0,0,0)';
                            updateStops(next);
                          }}
                        />
                      </td>

                      {/* Clear */}
                      <td style={{ paddingBottom: 6 }}>
                        <button
                          type="button"
                          className="btn-white soft"
                          style={{ fontSize: '0.65rem', padding: '2px 6px' }}
                          onClick={() => {
                            const next = [...gradientStops];
                            next[index] = 'rgba(0,0,0,0)';
                            updateStops(next);
                          }}
                        >
                          Clear
                        </button>
                      </td>

                      {/* Remove */}
                      <td
                        style={{
                          width: 30,
                          paddingBottom: 6,
                          textAlign: 'center'
                        }}
                      >
                        <button
                          type="button"
                          className="btn-white soft"
                          disabled={gradientStops.length <= 2}
                          style={{ width: 24, height: 24 }}
                          onClick={() => {
                            const next = gradientStops.filter((_, i) => i !== index);
                            updateStops(next);
                          }}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <button
              type="button"
              className="btn-white soft"
              style={{ marginTop: 6 }}
              onClick={() => {
                const next = [...gradientStops, '#FFFFFF'];
                updateStops(next);
              }}
            >
              Add Stop
            </button>
            <br/>
          </Form.Group>

        </>
      )}
    </>
  );
}
